/**
 * §18.2 — React Router across the three interfaces.
 *
 * §22.1 asks for code splitting by player, facilitator, and display route. The
 * player route is the only one loaded eagerly, because it is the one on a phone
 * over venue Wi-Fi (§22.2).
 */

import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "@tq/ui/tokens.css";
import "./app.css";
import { PlayerApp } from "./player/PlayerApp.js";
import { Landing } from "./Landing.js";
import { ErrorBoundary } from "./ErrorBoundary.js";

const FacilitatorApp = lazy(() =>
  import("./facilitator/FacilitatorApp.js").then((module) => ({ default: module.FacilitatorApp })),
);
const DisplayApp = lazy(() =>
  import("./display/DisplayApp.js").then((module) => ({ default: module.DisplayApp })),
);

function Loading(): React.ReactElement {
  return (
    <div className="tq-loading" role="status" aria-live="polite">
      Loading…
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <BrowserRouter>
      {/* A render error must show something a player can act on, never a blank
          screen while the projector plays on. */}
      <ErrorBoundary>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<Landing />} />

            {/* §5.3 — the first screen a player sees is the join screen. There is
                no mode selection, because solo play is out of scope (§3.2). */}
            <Route path="/join" element={<PlayerApp />} />
            <Route path="/join/:sessionCode" element={<PlayerApp />} />
            {/* §35.4 — the join URL carries tenant context. */}
            <Route path="/t/:tenantSlug/join/:sessionCode" element={<PlayerApp />} />
            <Route path="/play/:sessionId" element={<PlayerApp />} />

            {/* §5.2 — the private facilitator console. */}
            <Route path="/host/*" element={<FacilitatorApp />} />

            {/* §5.1 — the public projector display. */}
            <Route path="/display/:sessionId" element={<DisplayApp />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  </StrictMode>,
);
